// Step 13 — Deal Actions panel.
//
// Groups actions into: NEEDS REVIEW / OPEN / BLOCKED / READY /
// RECENTLY COMPLETED. Read + human-controlled workflow only —
// there is deliberately NO "send" or "notify buyer" button.

import { useCallback, useEffect, useMemo, useState } from "react";

import {
  approveDealAction,
  blockDealAction,
  cancelDealAction,
  completeDealAction,
  listDealActions,
  rejectDealAction,
  resolveDealActionBlockers,
  startDealAction,
} from "./api";
import DealActionDetail from "./DealActionDetail";

const STATUS_LABEL = {
  proposed: "Föreslagen",
  pending_review: "Under granskning",
  approved: "Godkänd",
  in_progress: "Pågår",
  blocked: "Blockerad",
  completed: "Klar",
  rejected: "Avvisad",
  cancelled: "Avbruten",
  superseded: "Ersatt",
};

const PRIORITY_LABEL = {
  low: "Låg",
  medium: "Medel",
  high: "Hög",
  critical: "Kritisk",
};

const READINESS_LABEL = {
  ready: "Redo",
  needs_information: "Behöver info",
  needs_approval: "Behöver godkännande",
  blocked: "Blockerad",
  not_ready: "Ej redo",
};

export default function DealActionsPanel({ workspaceId }) {
  const [actions, setActions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(null); // {id} — opens detail

  const load = useCallback(async () => {
    if (!workspaceId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await listDealActions(workspaceId, { limit: 200 });
      setActions(res?.results || []);
    } catch (e) {
      setError(e?.body?.detail || e.message || "Kunde inte ladda åtgärder");
    } finally {
      setLoading(false);
    }
  }, [workspaceId]);

  useEffect(() => {
    load();
  }, [load]);

  // ── Grouping ──
  const groups = useMemo(() => {
    const g = {
      needs_review: [],   // proposed OR pending_review
      open: [],           // approved OR in_progress
      blocked: [],        // blocked
      ready: [],          // readiness=ready AND status in (approved,in_progress)
      completed: [],      // completed (limit 10)
    };
    for (const a of actions) {
      if (a.status === "proposed" || a.status === "pending_review") {
        g.needs_review.push(a);
      } else if (a.status === "blocked") {
        g.blocked.push(a);
      } else if (a.status === "approved" || a.status === "in_progress") {
        g.open.push(a);
        if (a.readiness === "ready") g.ready.push(a);
      } else if (a.status === "completed") {
        if (g.completed.length < 10) g.completed.push(a);
      }
    }
    return g;
  }, [actions]);

  // ── Verb dispatchers (used from row action bars) ──
  const runVerb = useCallback(
    async (fn, actionId, args) => {
      if (!workspaceId || !actionId) return;
      try {
        await fn(workspaceId, actionId, args || {});
        await load();
      } catch (e) {
        setError(e?.body?.detail || e.message || "Åtgärden misslyckades.");
      }
    },
    [workspaceId, load],
  );

  if (!workspaceId) return null;
  if (loading && actions.length === 0) {
    return (
      <div className="qai-da-panel">
        <div className="qai-da-header">Affärsåtgärder</div>
        <div className="qai-da-loading">Laddar…</div>
      </div>
    );
  }

  return (
    <div className="qai-da-panel">
      <div className="qai-da-header">
        <span>Affärsåtgärder</span>
        <span className="qai-da-header-total">{actions.length}</span>
      </div>

      {error && <div className="qai-da-error">{error}</div>}

      <Section title="Behöver granskning" items={groups.needs_review} empty="Inga förslag väntar på granskning.">
        {(a) => (
          <Row a={a} onOpen={() => setSelected({ id: a.id })}>
            <Btn kind="primary" onClick={() => runVerb(approveDealAction, a.id)}>Godkänn</Btn>
            <Btn kind="ghost" onClick={() => runVerb(rejectDealAction, a.id, { reason: "Avvisad från listvy" })}>Avvisa</Btn>
          </Row>
        )}
      </Section>

      <Section title="Öppna" items={groups.open} empty="Inga öppna åtgärder.">
        {(a) => (
          <Row a={a} onOpen={() => setSelected({ id: a.id })}>
            {a.status === "approved" && (
              <Btn kind="primary" onClick={() => runVerb(startDealAction, a.id)}>Starta</Btn>
            )}
            {a.status === "in_progress" && (
              <Btn kind="primary" onClick={() => runVerb(completeDealAction, a.id)}>Markera klar</Btn>
            )}
            <Btn kind="ghost" onClick={() => {
              const reason = window.prompt("Blockeringsorsak?");
              if (reason && reason.trim()) runVerb(blockDealAction, a.id, { reason: reason.trim() });
            }}>Blockera</Btn>
            <Btn kind="ghost" onClick={() => runVerb(cancelDealAction, a.id)}>Avbryt</Btn>
          </Row>
        )}
      </Section>

      <Section title="Blockerade" items={groups.blocked} empty="Inga blockerade åtgärder.">
        {(a) => (
          <Row a={a} onOpen={() => setSelected({ id: a.id })}>
            <Btn kind="primary" onClick={() => runVerb(resolveDealActionBlockers, a.id)}>Lös blockering</Btn>
          </Row>
        )}
      </Section>

      <Section title="Redo att utföra" items={groups.ready} empty="Inga redo-åtgärder just nu.">
        {(a) => (
          <Row a={a} onOpen={() => setSelected({ id: a.id })} />
        )}
      </Section>

      <Section title="Nyligen klara" items={groups.completed} empty="Inga klara åtgärder ännu.">
        {(a) => <Row a={a} onOpen={() => setSelected({ id: a.id })} />}
      </Section>

      {selected?.id && (
        <DealActionDetail
          workspaceId={workspaceId}
          actionId={selected.id}
          onClose={() => setSelected(null)}
          onChanged={load}
        />
      )}

      <div className="qai-da-disclaimer">
        Sigge skickar inte mejl, kontaktar inte köpare och ändrar inte
        affärssteg automatiskt. Åtgärder är förslag och arbetsposter —
        alla externa handlingar utförs manuellt.
      </div>
    </div>
  );
}

function Section({ title, items, empty, children }) {
  return (
    <section className="qai-da-section">
      <h4>{title} <span className="qai-da-count">{items.length}</span></h4>
      {items.length === 0 ? (
        <div className="qai-da-empty">{empty}</div>
      ) : (
        <ul className="qai-da-list">
          {items.map((a) => (
            <li key={a.id}>{children(a)}</li>
          ))}
        </ul>
      )}
    </section>
  );
}

function Row({ a, onOpen, children }) {
  return (
    <div className="qai-da-row">
      <button type="button" className="qai-da-row-open" onClick={onOpen}>
        <div className="qai-da-row-title">{a.title}</div>
        <div className="qai-da-row-meta">
          <span className={`qai-da-chip qai-da-chip-status qai-da-chip-status-${a.status}`}>
            {STATUS_LABEL[a.status] || a.status}
          </span>
          <span className={`qai-da-chip qai-da-chip-pri-${a.priority}`}>
            {PRIORITY_LABEL[a.priority] || a.priority}
          </span>
          <span className="qai-da-chip qai-da-chip-ready">
            {READINESS_LABEL[a.readiness] || a.readiness}
          </span>
          {a.acquirer_name && (
            <span className="qai-da-chip qai-da-chip-buyer">{a.acquirer_name}</span>
          )}
          {a.due_at && (
            <span className="qai-da-chip qai-da-chip-due">
              Deadline {new Date(a.due_at).toLocaleDateString("sv-SE")}
            </span>
          )}
          {a.blocker_count > 0 && (
            <span className="qai-da-chip qai-da-chip-blocked">
              {a.blocker_count} blockering{a.blocker_count > 1 ? "ar" : ""}
            </span>
          )}
        </div>
      </button>
      {children && <div className="qai-da-row-actions">{children}</div>}
    </div>
  );
}

function Btn({ kind = "ghost", onClick, children }) {
  return (
    <button
      type="button"
      className={`qai-da-btn qai-da-btn-${kind}`}
      onClick={onClick}
    >
      {children}
    </button>
  );
}
